import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import Swal from "sweetalert2";
import {
  clearSession,
  isTokenExpired,
  refreshToken,
  shouldRefreshToken,
  isLoggingOut,
  setLoggingOut,
} from "./tokenUtils";
import { DIALOG_COLORS } from "../components/ui/dialog";
import { API_ERROR_CODES, apiErrorCode } from "./apiErrorCodes";
import { isPlanLimitError, showPlanLimitNotice } from "./planLimitNotice";
import { scheduleSetupChangedAfterWrite } from "../setup/events";
import { resolveSecurityError } from "../security/securityInterceptor";
import {
  announceSandboxReadOnly,
  realCompanyId,
  SANDBOX_VIEW_HEADER,
  sandboxRequestMode,
} from "../features/sandbox/sandboxView";

// Este archivo es IDÉNTICO en CC-CONSOLE y WAYAK (interceptor de sesión
// unido). Lo único que cambia por tenant es `REQUEST_TIMEOUT_MS_BY_TENANT`.

// El project ID del emulador local sale de apps/v5/.env.development.
// Cualquier otro valor parte el Firestore en silencio.
const API_URL =
  import.meta.env.VITE_FIREBASE_API_URL ||
  "http://127.0.0.1:5001/cc-console-development/us-central1/wayakDevelopmentApi";

/**
 * Tiempo máximo de una petición JSON, por tenant (`VITE_THEME`).
 *
 * WAYAK lo subió a 60 s para operaciones que requieren búsquedas extensas en
 * Firestore; CC-CONSOLE se queda en 30 s (sus avisos de «el comando tardó
 * demasiado» cuentan con ese tope). Un tenant que no está en la tabla usa
 * 60 s. `apiFormData` (archivos) usa 60 s en todos.
 */
export const REQUEST_TIMEOUT_MS_BY_TENANT: Readonly<Record<string, number>> = {
  "coding-cloud": 30000,
};
export const DEFAULT_REQUEST_TIMEOUT_MS = 60000;

export function requestTimeoutFor(tenant: unknown): number {
  const key = typeof tenant === "string" ? tenant.trim().toLowerCase() : "";
  return REQUEST_TIMEOUT_MS_BY_TENANT[key] ?? DEFAULT_REQUEST_TIMEOUT_MS;
}

// Flag para evitar múltiples alertas de sesión expirada
let isShowingSessionExpiredAlert = false;

// Admin abort signal: cancels in-flight GET requests when company changes
let _adminAbortSignal: AbortSignal | null = null;

export function setAdminAbortSignal(signal: AbortSignal | null) {
  _adminAbortSignal = signal;
}

export function isAbortError(error: unknown): boolean {
  return (
    axios.isCancel(error) ||
    (error instanceof Error && error.name === "AbortError")
  );
}

export function isAxiosClientTimeout(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response != null) return false;
  const code = error.code;
  const msg = (error.message || "").toLowerCase();
  return code === "ECONNABORTED" || msg.includes("timeout");
}

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: requestTimeoutFor(import.meta.env.VITE_THEME),
  headers: {
    "Content-Type": "application/json",
  },
});

const getCurrentLang = (): string => {
  const i18nLang = localStorage.getItem("i18nextLng");
  if (i18nLang) {
    return i18nLang.split("-")[0];
  }
  return navigator.language.split("-")[0] || "es";
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Si estamos en proceso de logout, cancelar la petición silenciosamente
    if (isLoggingOut()) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    // Rutas públicas que no requieren autenticación
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      config.url?.includes(route),
    );

    // Si es una ruta pública, no agregar headers de autenticación
    if (isPublicRoute) {
      return config;
    }

    // Vista del Sandbox: lo de la cuenta va con la compañía real, las lecturas
    // llevan el encabezado y una escritura de negocio no sale de aquí.
    const sandboxMode = sandboxRequestMode(config.method, config.url, API_URL);
    if (sandboxMode === "blocked") {
      announceSandboxReadOnly();
      return Promise.reject(new axios.Cancel("Sandbox read-only"));
    }

    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");
    const companyId = sandboxMode === "view" ? localStorage.getItem("companyId") : realCompanyId();
    const userId = localStorage.getItem("userId");

    // Bandera explícita para flujos reseller multi-tenant. Cuando es `true`
    // omitimos por completo la inyección de `x-company-id` en headers y
    // `company_id` en query/body. Necesario porque el truco de pasar
    // `headers: { "x-company-id": "" }` no funcionaba: el chequeo falsy de
    // abajo lo sobreescribía con el companyId del localStorage.
    const skipCompanyScope =
      (config as InternalAxiosRequestConfig & { _skipCompanyScope?: boolean })
        ._skipCompanyScope === true;

    if (token && isTokenExpired(token)) {
      clearSession();
      return Promise.reject(new axios.Cancel("Token expired"));
    }

    if (token && shouldRefreshToken(token) && !isRefreshing) {
      isRefreshing = true;
      const refreshed = await refreshToken();
      isRefreshing = false;

      if (refreshed) {
        const newToken = localStorage.getItem("token");
        if (newToken) {
          onTokenRefreshed(newToken);
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (sessionId) {
      config.headers["x-session-id"] = sessionId;
    }

    if (userId) {
      config.headers["x-user-id"] = userId;
    }

    if (sandboxMode === "view") {
      config.headers[SANDBOX_VIEW_HEADER] = "1";
    }

    if (companyId && !skipCompanyScope) {
      if (!config.headers["x-company-id"]) {
        config.headers["x-company-id"] = companyId;
      }
      if (config.method === "post" || config.method === "put") {
        // No agregar company_id al endpoint /company (para gestión de compañías)
        const isCompanyEndpoint = config.url?.includes("/company");

        if (typeof FormData !== "undefined" && config.data instanceof FormData) {
          // Un FormData no se puede esparcir en un objeto: `{ ...formData }`
          // lo vacía y el archivo nunca llega al servidor. Se le AGREGA el
          // campo, igual que hace `apiFormData`, y sigue el resto del
          // interceptor (antes WAYAK salía aquí y la petición iba sin `lang`).
          if (!isCompanyEndpoint && !config.data.has("company_id")) {
            config.data.append("company_id", companyId);
          }
        } else if (config.data && !isCompanyEndpoint) {
          if (typeof config.data === "string") {
            try {
              config.data = JSON.parse(config.data);
            } catch (e) {
              console.error("Error parsing data:", e);
            }
          }

          // Solo agregar company_id si no existe en los datos (no sobrescribir)
          // Verificar explícitamente undefined para no sobrescribir null o string vacío
          if (
            typeof config.data === "object" &&
            config.data !== null &&
            config.data.company_id === undefined
          ) {
            config.data = {
              ...config.data,
              company_id: companyId,
            };
          }
        } else if (!config.data && !isCompanyEndpoint) {
          config.data = { company_id: companyId };
        }
      }
    }

    config.params = {
      ...config.params,
      lang: config.params?.lang || getCurrentLang(),
    };

    // Agregar company_id a los parámetros para peticiones GET (solo si no fue establecido explícitamente)
    // Excluir endpoint /company para gestión de compañías
    const isCompanyEndpoint = config.url?.includes("/company");
    if (
      companyId &&
      config.method === "get" &&
      !isCompanyEndpoint &&
      !skipCompanyScope
    ) {
      if (!config.params?.company_id) {
        config.params = {
          ...config.params,
          company_id: companyId,
        };
      }
    }

    if (_adminAbortSignal && !config.signal && config.method === "get") {
      config.signal = _adminAbortSignal;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// ¿El error dice que la SESIÓN ya no vale? Una sola regla para los dos
// clientes (JSON y archivos) y para los dos forks:
//
//   1. Código estable (`meta.code` / `meta.messageKey`, que el errorHandler
//      repite con el mismo valor) de sesión inválida ⇒ sí.
//   2. Cualquier OTRO código estable ⇒ no: es un error de autorización o de
//      negocio y la sesión sigue buena. Así, los 403 cuyo texto habla de
//      sesiones (INTERACTIVE_SESSION_REQUIRED «…una sesión iniciada por una
//      persona…», SESSION_FORBIDDEN, SESSION_REVOKED, PERMISSION_DENIED) no
//      cierran la sesión de quien sólo carecía de permiso.
//   3. Sin código, con el sobre `{ meta }`: el mensaje tiene que ser,
//      COMPLETO, uno de los de validateSession (`SESSION_FAILURE_MESSAGES`).
//      Antes WAYAK buscaba «session»/«sesión» como subcadena y cerraba la
//      sesión con cualquier mensaje que la mencionara.
//   4. Sin sobre (forma heredada, plana): además del mensaje exacto se
//      conserva el criterio amplio original por subcadena. Hoy ningún
//      endpoint de la API responde así con un 403.
//
// Quién llama decide el status: 401 siempre cierra; 403 sólo con esta regla;
// 404 sólo con el código (paso 1), nunca por texto.
// ---------------------------------------------------------------------------

// Mensajes con los que el backend rechaza una SESIÓN (middlewares/
// validateSession.js → dictionary es.json / en.json: sessionClosed,
// UnauthorizedSession, NoActiveSession). Se comparan completos, no por
// subcadena: hay 403 de AUTORIZACIÓN cuyo texto también habla de sesiones
// ("…solo se cambian desde una sesión interactiva…", mesa de servicio) y
// confundirlos cerraría la sesión de quien sólo carece de permisos.
const SESSION_FAILURE_MESSAGES = [
  "sesión expirada",
  "sesión no autorizada",
  "no tienes una sesión activa. por favor inicia sesión nuevamente.",
  "session expired",
  "unauthorized session",
  "you don't have an active session. please log in again.",
  // Red de seguridad: si el idioma pedido no está en el diccionario, i18next
  // devuelve la clave tal cual. Un fallo de sesión debe seguir cerrándola.
  "sessionclosed",
  "unauthorizedsession",
  "noactivesession",
];

export function isSessionFailureMessage(message?: string | null): boolean {
  if (typeof message !== "string") return false;
  return SESSION_FAILURE_MESSAGES.includes(message.trim().toLowerCase());
}

// Códigos estables con los que el backend rechaza una SESIÓN (middlewares/
// auth.js, validateSession.js, utils/sessionErrors.js), más las claves
// heredadas que algunos endpoints viejos mandaban en `messageKey`.
const SESSION_INVALID_CODES = [
  "SESSION_CLOSED",
  "SESSION_NOT_FOUND",
  "SESSION_UNAUTHORIZED",
  "NO_ACTIVE_SESSION",
  "SessionNotFound",
  "SessionInactive",
  "InvalidSession",
];

type ErrorBody =
  | {
      meta?: { code?: string; messageKey?: string; message?: string };
      code?: string;
      messageKey?: string;
      message?: string;
    }
  | undefined;

const bodyOf = (error: AxiosError): ErrorBody => {
  const data = error.response?.data;
  return data && typeof data === "object" ? (data as ErrorBody) : undefined;
};

/** Identificador estable del error, venga en el sobre o en la forma plana. */
const stableCodeOf = (body: ErrorBody): string | undefined => {
  const code = body?.meta?.code ?? body?.meta?.messageKey ?? body?.code ?? body?.messageKey;
  return typeof code === "string" && code ? code : undefined;
};

/** ¿El error trae uno de los códigos estables de sesión inválida? */
export function isSessionInvalidCode(error: AxiosError): boolean {
  const code = stableCodeOf(bodyOf(error));
  return code !== undefined && SESSION_INVALID_CODES.includes(code);
}

/** ¿El error dice que la sesión ya no vale? (ver la regla de arriba) */
export function isSessionInvalidError(error: AxiosError): boolean {
  const body = bodyOf(error);
  const code = stableCodeOf(body);
  if (code !== undefined) return SESSION_INVALID_CODES.includes(code);
  if (body?.meta) return isSessionFailureMessage(body.meta.message);
  const legacy = typeof body?.message === "string" ? body.message : "";
  if (isSessionFailureMessage(legacy)) return true;
  const text = legacy.toLowerCase();
  return text.includes("session") || text.includes("sesión");
}

/**
 * ¿Hubo alguna vez una sesión que pudiera expirar? (WAYAK #855)
 *
 * Sin token guardado, no. Y sin eso el aviso es una mentira: a quien abre la
 * aplicación por primera vez —o vuelve después de cerrar sesión— le salía
 * «Sesión expirada. Tu sesión ha expirado o fue cerrada» encima del login,
 * antes de haber entrado nunca. Basta con que algo pida un endpoint
 * autenticado desde la pantalla de entrada y conteste 401. No depende de la
 * ruta sino del hecho, que es lo que el mensaje afirma.
 */
const nuncaHuboSesion = () =>
  typeof window !== "undefined" && !localStorage.getItem("token");

/**
 * Páginas que se abren SIN cuenta. Quien llega aquí no tiene sesión, así que
 * no hay ninguna que pueda expirar.
 *
 * Hace falta porque estas páginas llaman endpoints del cliente autenticado
 * —`/iconsList`, para dibujar los vehículos— y ese 401 disparaba «Sesión
 * expirada» encima del mapa, con un botón «Ir al Login», a alguien que sólo
 * recibió un enlace para ver dónde va una unidad.
 */
const PAGINAS_PUBLICAS = [
  "/trackingLinks/public",
  "/tracking-replay/recording",
  "/espacios/",
  "/shop/",
  "/soporte",
];

const enPaginaPublica = () =>
  typeof window !== "undefined" &&
  PAGINAS_PUBLICAS.some((ruta) => window.location.pathname.startsWith(ruta));

/**
 * Muestra alerta de sesión expirada y redirige al login
 */
const showSessionExpiredAlert = async () => {
  if (isShowingSessionExpiredAlert || isLoggingOut()) return;
  // Sin sesión no hay nada que expire: ver `nuncaHuboSesion`.
  if (nuncaHuboSesion()) return;
  // En una página pública no se avisa ni se redirige: no había sesión.
  if (enPaginaPublica()) return;

  isShowingSessionExpiredAlert = true;
  setLoggingOut(true);

  // Cerrar cualquier SweetAlert abierto
  Swal.close();

  const lang = localStorage.getItem("i18nextLng")?.split("-")[0] || "es";
  const messages = {
    es: {
      title: "Sesión expirada",
      text: "Tu sesión ha expirado o fue cerrada. Por favor, inicia sesión nuevamente.",
      button: "Ir al Login",
    },
    en: {
      title: "Session expired",
      text: "Your session has expired or was closed. Please log in again.",
      button: "Go to Login",
    },
  };

  const msg = messages[lang as keyof typeof messages] || messages.es;

  await clearSession(false); // No intentar cerrar sesión en backend (ya no es válida)

  await Swal.fire({
    icon: "warning",
    title: msg.title,
    text: msg.text,
    confirmButtonText: msg.button,
    confirmButtonColor: DIALOG_COLORS.success,
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  isShowingSessionExpiredAlert = false;
  setLoggingOut(false);
  window.location.href = "/";
};

/**
 * 403 por falta de permiso (no por sesión): el backend lo marca con
 * `meta.code = "PERMISSION_DENIED"` (requireChatPermission,
 * requireIntegrationPermission, requireIntegrationKeysWriter) o con
 * `PLATFORM_ADMIN_REQUIRED` cuando la acción es exclusiva de Sistemas
 * Internos (#642), `ENTITLEMENT_REQUIRED` o `COMPANY_SCOPE_FORBIDDEN`. La
 * sesión sigue siendo válida: quien llamó decide cómo mostrarlo y NO se
 * cierra la sesión. Catálogo en `utils/apiErrorCodes.ts`.
 */
export const isPermissionDenied = (error: unknown): boolean => {
  const code = apiErrorCode(error);
  return (
    code === API_ERROR_CODES.PERMISSION_DENIED ||
    code === API_ERROR_CODES.PLATFORM_ADMIN_REQUIRED ||
    code === API_ERROR_CODES.ENTITLEMENT_REQUIRED ||
    code === API_ERROR_CODES.COMPANY_SCOPE_FORBIDDEN ||
    code === API_ERROR_CODES.SANDBOX_READ_ONLY
  );
};

// Puertas de entrada: su 403 de dominio lo maneja la propia pantalla de
// login/registro (mensaje específico + signOut), no la alerta de sesión.
const AUTH_ENTRY_ROUTES = ["/login", "/auth/register", "/auth/link-google"];

/**
 * 403 `EMAIL_DOMAIN_NOT_ALLOWED` fuera del login (#636): la sesión es de un
 * correo que ya no puede usar este tenant (p. ej. abierta antes de la
 * restricción). No hay acción posible dentro de la consola ⇒ se trata como
 * fin de sesión (cierra también Firebase vía clearSession). En un tenant sin
 * restricción de dominio (WAYAK hoy) el backend nunca lo manda.
 */
export const isEmailDomainSessionFailure = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  apiErrorCode(error) === API_ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED &&
  !AUTH_ENTRY_ROUTES.some((route) => error.config?.url?.includes(route));

api.interceptors.response.use(
  (response) => {
    // Un alta o edición exitosa puede destrabar una app: la barra «falta
    // configurar» se re-evalúa (agrupado, #1096). Sin quien escuche el evento
    // (un fork sin esa barra) no tiene ningún efecto.
    scheduleSetupChangedAfterWrite(response.config?.method, response.config?.url);
    return response;
  },
  async (error: AxiosError) => {
    // Handle 304 Not Modified as successful response
    if (error.response?.status === 304) {
      return error.response;
    }

    // Si es una cancelación por logout, no propagar el error
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Si estamos en proceso de logout o mostrando alerta, ignorar errores
    if (isLoggingOut() || isShowingSessionExpiredAlert) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Rutas públicas que no requieren manejo de sesión
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      originalRequest.url?.includes(route),
    );

    // Si es una ruta pública, no manejar errores de sesión
    if (isPublicRoute) {
      return Promise.reject(error);
    }

    // Verificar si es un error de sesión inválida/expirada (regla de arriba).
    const isSessionInvalid = isSessionInvalidError(error);

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshed = await refreshToken();
          isRefreshing = false;

          if (refreshed) {
            const newToken = localStorage.getItem("token");
            if (newToken) {
              onTokenRefreshed(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            }
          }
        } catch (refreshError) {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      // No se pudo refrescar el token, mostrar alerta
      await showSessionExpiredAlert();
      return Promise.reject(error);
    }

    // 2FA y verificación de correo: a la sesión le falta un paso (un código
    // antes de una operación de riesgo, el reto de inicio, activar 2FA o
    // verificar el correo del alta). NO es una sesión caída: se resuelve y la
    // petición original se repite. Va antes de las ramas de 403 de abajo para
    // que ninguna lo confunda con un fallo de sesión.
    const security = await resolveSecurityError(error, (config) =>
      api(config as InternalAxiosRequestConfig),
    );
    if (security) {
      return security.retried ? security.response : Promise.reject(error);
    }

    // Tope del plan (CC-CONSOLE #1613): no es un fallo de sesión. Se avisa con
    // «Mejorar plan» y la petición se rechaza igual (quien llamó decide si
    // muestra algo más). Se espera al aviso para que otro Swal no lo tape.
    if (error.response?.status === 402 && isPlanLimitError(error)) {
      await showPlanLimitNotice(error);
      return Promise.reject(error);
    }

    // Un 403 tiene dos significados muy distintos y la regla es UNA sola:
    //   • autorización: la sesión es buena pero al usuario le falta permiso
    //     para ESA acción (permisos por rol del chat #524; la mesa de servicio
    //     respondiendo 403 a quien no es agente, #374). Es un error de
    //     pantalla: la sesión NO se toca y quien llamó decide cómo mostrarlo
    //     (toast, mensaje en línea…).
    //   • autenticación: la sesión ya no vale (validateSession) ⇒ se cierra
    //     la sesión y se avisa, como siempre.
    // Se reconocen DOS señales de autorización, no una: la explícita
    // (`isPermissionDenied`) y, por defecto, cualquier 403 que no cumpla la
    // regla de sesión inválida de arriba.
    if (error.response?.status === 403 && isPermissionDenied(error)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      if (isSessionInvalid || isEmailDomainSessionFailure(error)) {
        await showSessionExpiredAlert();
      }
      return Promise.reject(error);
    }

    // 404 de SESIÓN (validateSession: la sesión de `x-session-id` ya no
    // existe, p. ej. se borró al cerrarla en otro dispositivo). Sólo con el
    // código estable: un 404 cualquiera es «no encontrado» de pantalla, y el
    // «Sesión no encontrada» sin código de Perfil → Sesiones habla de OTRA
    // sesión, no de la propia.
    if (error.response?.status === 404 && isSessionInvalidCode(error)) {
      await showSessionExpiredAlert();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;

export const apiFormData = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

apiFormData.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (isLoggingOut()) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    // Rutas públicas que no requieren autenticación
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      config.url?.includes(route),
    );

    // Si es una ruta pública, no agregar headers de autenticación
    if (isPublicRoute) {
      return config;
    }

    const sandboxMode = sandboxRequestMode(config.method, config.url, API_URL);
    if (sandboxMode === "blocked") {
      announceSandboxReadOnly();
      return Promise.reject(new axios.Cancel("Sandbox read-only"));
    }

    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");
    const companyId = sandboxMode === "view" ? localStorage.getItem("companyId") : realCompanyId();

    if (token && isTokenExpired(token)) {
      clearSession();
      return Promise.reject(new axios.Cancel("Token expired"));
    }

    if (sandboxMode === "view") {
      config.headers[SANDBOX_VIEW_HEADER] = "1";
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (sessionId) {
      config.headers["x-session-id"] = sessionId;
    }

    if (companyId) {
      config.headers["x-company-id"] = companyId;

      if (config.method === "post" || config.method === "put") {
        if (config.data instanceof FormData) {
          // `has`: una petición que se repite (tras el reto de 2FA) ya lo trae.
          if (!config.data.has("company_id")) {
            config.data.append("company_id", companyId);
          }
        } else if (config.data && typeof config.data === "object") {
          config.data = {
            ...config.data,
            company_id: companyId,
          };
        } else if (!config.data) {
          config.data = { company_id: companyId };
        }
      }
    }

    config.params = {
      ...config.params,
      lang: config.params?.lang || getCurrentLang(),
    };

    // Agregar company_id a los parámetros para peticiones GET
    if (companyId && config.method === "get") {
      config.params = {
        company_id: companyId,
        ...config.params,
      };
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiFormData.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Si es una cancelación por logout, no propagar el error
    if (
      axios.isCancel(error) ||
      isLoggingOut() ||
      isShowingSessionExpiredAlert
    ) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    const originalRequest = error.config as InternalAxiosRequestConfig;

    // Rutas públicas que no requieren manejo de sesión
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      originalRequest.url?.includes(route),
    );

    // Si es una ruta pública, no manejar errores de sesión
    if (isPublicRoute) {
      return Promise.reject(error);
    }

    // 2FA: igual que en el cliente JSON (ver arriba).
    const security = await resolveSecurityError(error, (config) =>
      apiFormData(config as InternalAxiosRequestConfig),
    );
    if (security) {
      return security.retried ? security.response : Promise.reject(error);
    }

    // Mismo criterio que el cliente JSON: un 403 de AUTORIZACIÓN (permisos por
    // rol del chat #524, o subir evidencia a un ticket de otra compañía sin ser
    // agente de la mesa) es un error de pantalla, no el final de la sesión.
    if (error.response?.status === 403 && isPermissionDenied(error)) {
      return Promise.reject(error);
    }

    // Y el mismo status por rama: 401 siempre, 403 con la regla de sesión,
    // 404 sólo con el código. Antes WAYAK cerraba la sesión con cualquier
    // status (un 400 o un 500) si el mensaje decía «session»/«sesión».
    if (
      error.response?.status === 401 ||
      (error.response?.status === 403 && isSessionInvalidError(error)) ||
      (error.response?.status === 404 && isSessionInvalidCode(error)) ||
      isEmailDomainSessionFailure(error)
    ) {
      await showSessionExpiredAlert();
    }
    return Promise.reject(error);
  },
);
